import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Globe2 } from 'lucide-react';
import type { SiteToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useSitesAdminList, useSiteMutations } from '~/hooks/use-sites-admin';

/** Sites CRUD surface — Code/Name/CrawlState/DefLanguage/DefCurrency/AppendPrefix/Visible/Sort. */
export function SitesGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useSitesAdminList();
	const { remove } = useSiteMutations();

	const [deleteRow, setDeleteRow] = useState<SiteToken | null>(null);

	const yesNo = (v?: boolean) => (v ? t('common.yes') : t('common.no'));

	const columns = useMemo<SystemGridColumn<SiteToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.sites.columns.code'),
				width: '120px',
				sortValue: s => s.cd ?? '',
				exportValue: s => s.cd ?? '',
				render: s => <span className="truncate font-mono text-xs font-medium text-foreground">{s.cd || '—'}</span>
			},
			{
				key: 'name',
				header: t('system.sites.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.nm,
				exportValue: s => s.nm,
				render: s => <span className="truncate text-sm font-medium text-foreground">{s.nm}</span>
			},
			{
				key: 'crawlState',
				header: t('system.sites.columns.crawlState'),
				width: '140px',
				sortValue: s => s.cstx ?? '',
				exportValue: s => s.cstx ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.cstx || '—'}</span>
			},
			{
				key: 'language',
				header: t('system.sites.columns.defLanguage'),
				width: '120px',
				sortValue: s => s.dlnm ?? '',
				exportValue: s => s.dlnm ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.dlnm || '—'}</span>
			},
			{
				key: 'currency',
				header: t('system.sites.columns.defCurrency'),
				width: '120px',
				sortValue: s => s.dcnm ?? '',
				exportValue: s => s.dcnm ?? '',
				render: s => <span className="truncate text-sm text-foreground-muted">{s.dcnm || '—'}</span>
			},
			{
				key: 'appendPrefix',
				header: t('system.sites.columns.appendLanguagePrefix'),
				width: '90px',
				align: 'center',
				sortValue: s => (s.alp ? 1 : 0),
				exportValue: s => yesNo(s.alp),
				render: s => (s.alp ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'visible',
				header: t('system.sites.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: s => (s.iv ? 1 : 0),
				exportValue: s => yesNo(s.iv),
				render: s => (s.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.sites.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: s => s.si ?? 0,
				exportValue: s => s.si ?? 0,
				render: s => <span className="text-sm tabular-nums text-foreground-muted">{s.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<SiteToken>
				gridId="sites"
				title={t('system.sites.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={s => s.id ?? s.nm}
				columns={columns}
				searchPredicate={(s, q) => `${s.nm ?? ''} ${s.cd ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Globe2 size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.sites.add')}
				onAdd={() => navigate('/system/sites/create')}
				onEdit={s => navigate(`/system/sites/${s.id}`)}
				onDelete={setDeleteRow}
				exportFilename="sites"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.sites.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.sites.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
